import type { PointToPoint, Rate } from '@api/useFreight/types'
import { useRateManagement as useApiRateManagement } from '@api/useFreight/useFreight'
import { useQueryClient } from '@tanstack/react-query'
import { Form, message } from 'antd'
import dayjs from 'dayjs'
import { useState, useEffect, useRef } from 'react'
import type { EditRateFormValues } from '../types'
import { useLocalStorage } from '../utils'

/**
 * Custom hook for managing point-to-point rate operations
 * @param pointToPoint The point-to-point object
 * @param setPointToPoint Function to update point-to-point state
 * @param onSuccess Optional callback on successful operations
 * @returns Rate management operations
 */
export function useRateManagement(
  pointToPoint: PointToPoint,
  setPointToPoint: (p: PointToPoint) => void,
  onSuccess?: () => void
) {
  const queryClient = useQueryClient()
  const { useDeletePointToPointRate, useUpdatePointToPointRate } = useApiRateManagement()

  const deleteMutation = useDeletePointToPointRate()
  const updateMutation = useUpdatePointToPointRate()

  /**
   * Delete a point-to-point rate
   * @param rate The rate to delete
   */
  const deleteRate = async (rate: Rate) => {
    try {
      const pointToPointId = pointToPoint._id || pointToPoint.id

      const updatedPointToPoint = await deleteMutation.mutateAsync({
        point_to_point_id: pointToPointId,
        effective_date: rate.effective_from,
      })

      // Update local state
      setPointToPoint(updatedPointToPoint)

      message.success('Rate deleted successfully')

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (error) {
      console.error('Error deleting rate:', error)
      message.error('Failed to delete rate')
      return false
    }
  }

  /**
   * Edit a point-to-point rate
   * @param rate The rate being edited
   * @param values New values for the rate
   * @param hasEndDate Whether the rate has an end date
   * @param showDistance Whether to include distance
   */
  const editRate = async (rate: Rate, values: EditRateFormValues, hasEndDate: boolean, showDistance: boolean) => {
    try {
      // Validate rate is not zero
      if (values.rate <= 0) {
        message.error('Rate must be greater than 0')
        return false
      }

      const effectiveDate = values.effective_date.toISOString()
      const effectiveToDate = hasEndDate && values.effective_to ? values.effective_to.toISOString() : null

      const updatedPointToPoint = await updateMutation.mutateAsync({
        point_to_point_id: pointToPoint._id || pointToPoint.id,
        effective_date: effectiveDate,
        effective_to_date: effectiveToDate,
        rate: values.rate,
        distance: showDistance && values.distance ? Number(values.distance) : undefined,
        rate_id: rate.id,
      })

      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ['ratebook/p2p'] })

      message.success('Rate updated successfully')

      // Update local state
      setPointToPoint(updatedPointToPoint)

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (error) {
      console.error('Failed to update rate:', error)
      message.error('Failed to update rate')
      return false
    }
  }

  return {
    deleteRate,
    editRate,
    deleteMutation,
    updateMutation,
  }
}

/**
 * Custom hook for the edit rate form
 * @param rate The rate being edited
 * @param onSuccess Optional callback on successful edit
 * @returns Form state and handlers
 */
export function useEditRateForm(rate: Rate, onSuccess?: () => void) {
  const [form] = Form.useForm<EditRateFormValues>()
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [hasEndDate, setHasEndDate] = useLocalStorage('point-to-point-has-end-date', false)
  const [showDistance, setShowDistance] = useLocalStorage('point-to-point-show-distance', false)
  const hasInitializedRef = useRef(false)

  // Initialize form and toggles when popover opens
  useEffect(() => {
    if (popoverVisible && !hasInitializedRef.current) {
      // Initialize form values
      form.setFieldsValue({
        effective_date: dayjs(rate.effective_from),
        effective_to: rate.effective_to ? dayjs(rate.effective_to) : null,
        rate: rate.rate,
        distance: rate.distance || null,
      })

      // Initialize toggle states based on rate data
      const hasEffectiveTo = !!rate.effective_to
      setHasEndDate(hasEffectiveTo)
      setShowDistance(!!rate.distance)

      hasInitializedRef.current = true
    }

    // Reset the initialized flag when popover closes
    if (!popoverVisible) {
      hasInitializedRef.current = false
    }
  }, [popoverVisible, rate, form, setHasEndDate, setShowDistance])

  return {
    form,
    popoverVisible,
    setPopoverVisible,
    hasEndDate,
    setHasEndDate,
    showDistance,
    setShowDistance,
  }
}
